import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chart from 'react-apexcharts';
import Humanize from 'humanize-plus';
import authStyles from '../authStyles';
import StatTile from '../../../components/StatTile/StatTile';
import DataTable from '../../../components/DataTable/MuiDataTable';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import request from '../../../utilities/request';
import * as config from './../../../config';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as kycActions from '../../../store/kyc/kycActions';
import * as capTableActions from '../../../store/capTable/capTableActions';
import { formatNumber } from '../../../utilities/utils';
import { useThemeMode } from '../../../theme/ThemeModeContext';

const Overview = (props) => {
    const classes = authStyles();
    const { mode } = useThemeMode();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [deleteHolderData, setDeleteHolderData] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [chartData, setChartData] = useState(null);

    useEffect(() => {
        if (!props.hideHoldersTable) {
            props.capTableActions.getShareholders();
        }
    }, [props.hideHoldersTable]);

    useEffect(() => {
        if (props.kyc) {
            setChartData({
                series: [
                    Number(
                        (props.kyc.total_kyc /
                            props.kyc.total_shareholders) *
                            100
                    ),
                    Number(
                        ((props.kyc.total_shareholders -
                            props.kyc.total_kyc) /
                            props.kyc.total_shareholders) *
                            100
                    ),
                ],
                labels: ["KYC'd", "Not KYC'd"],
            });
        }
    }, [props.kyc]);
    useEffect(props.kycActions.getKyc, []);

    const displayDeleteHolderDialog = (row) => {
        setDeleteHolderData(row);
    };

    const closeDeleteHolderDialog = () => {
        setDeleteHolderData(null);
    };

    const deleteHolder = () => {
        setIsDeleting(true);
        const values = {
            ...deleteHolderData,
            shares:
                typeof deleteHolderData.shares === 'string'
                    ? deleteHolderData.shares.replace(/,/g, '')
                    : deleteHolderData.shares,
        };
        request
            .delete(
                `${config.API_BASE_URL}/shareholders/category/${deleteHolderData.share_class_category_id}/delete`,
                { data: values }
            )
            .then(() => {
                enqueueSnackbar('Holder has been deleted successfully', {
                    variant: 'success',
                });
                props.capTableActions.getShareholders();
                closeDeleteHolderDialog();
            })
            .catch(() => {
                enqueueSnackbar(
                    'Error occurred while trying to delete holder.',
                    { variant: 'error' }
                );
            })
            .finally(() => {
                setIsDeleting(false);
            });
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography className={classes.title}>
                    Investor Overview
                </Typography>
                {props.kyc?.loading ? (
                    <Grid container item xs={12} justifyContent="center">
                        <CircularProgress />
                    </Grid>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '24px',
                            marginTop: '12px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '12px',
                                flex: '1 1 420px',
                            }}
                        >
                            <StatTile
                                label="Total Holders"
                                value={props.kyc?.total_shareholders ?? 0}
                            />
                            <StatTile
                                label="Total Investors Registered"
                                value={
                                    props.kyc?.total_investors_registered ?? 0
                                }
                            />
                            <StatTile
                                label="Total KYC'd"
                                value={props.kyc?.total_kyc ?? 0}
                            />
                            <StatTile
                                label="Total not KYC'd"
                                value={props.kyc?.total_not_kyc ?? 0}
                            />
                        </Box>
                        {chartData && (
                            <Box
                                sx={{
                                    width: 200,
                                    flex: '0 0 auto',
                                    filter:
                                        mode === 'dark'
                                            ? 'drop-shadow(0 4px 10px rgba(0,0,0,0.45))'
                                            : 'drop-shadow(0 4px 10px rgba(20,30,45,0.12))',
                                }}
                            >
                                <Chart
                                    key={mode}
                                    options={{
                                        chart: {
                                            background: 'transparent',
                                            foreColor:
                                                mode === 'dark'
                                                    ? '#9AA6B5'
                                                    : '#333333',
                                        },
                                        theme: {
                                            mode: mode,
                                        },
                                        labels: chartData.labels,
                                        colors: ['#3E9CD9', '#E05656'],
                                        fill: {
                                            type: 'gradient',
                                            gradient: {
                                                shade:
                                                    mode === 'dark'
                                                        ? 'dark'
                                                        : 'light',
                                                type: 'diagonal1',
                                                shadeIntensity: 0.35,
                                                opacityFrom: 1,
                                                opacityTo: 1,
                                            },
                                        },
                                        legend: {
                                            show: true,
                                            position: 'bottom',
                                            fontSize: '11px',
                                            fontFamily: 'Roboto',
                                            labels: {
                                                colors:
                                                    mode === 'dark'
                                                        ? '#9AA6B5'
                                                        : '#7D8696',
                                            },
                                            markers: {
                                                width: 8,
                                                height: 8,
                                                radius: 8,
                                            },
                                            itemMargin: {
                                                horizontal: 8,
                                                vertical: 2,
                                            },
                                        },
                                        stroke: {
                                            show: true,
                                            width: 3,
                                            colors: [
                                                mode === 'dark'
                                                    ? '#1B1F27'
                                                    : '#FFFFFF',
                                            ],
                                        },
                                        states: {
                                            hover: {
                                                filter: {
                                                    type: 'none',
                                                },
                                            },
                                            active: {
                                                filter: {
                                                    type: 'none',
                                                },
                                            },
                                        },
                                        tooltip: {
                                            theme: mode,
                                            fillSeriesColor: false,
                                        },
                                        plotOptions: {
                                            pie: {
                                                donut: {
                                                    size: '72%',
                                                    labels: {
                                                        show: true,
                                                        value: {
                                                            show: true,
                                                            fontSize: '20px',
                                                            fontWeight: 600,
                                                            offsetY: -4,
                                                            color:
                                                                mode === 'dark'
                                                                    ? '#EDEFF2'
                                                                    : '#333333',
                                                        },
                                                        label: {
                                                            show: true,
                                                            fontSize: '11px',
                                                            offsetY: 4,
                                                            color:
                                                                mode === 'dark'
                                                                    ? '#9AA6B5'
                                                                    : '#7D8696',
                                                        },
                                                        total: {
                                                            show: false,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    series={chartData.series}
                                    width="100%"
                                    type="donut"
                                    height={220}
                                />
                            </Box>
                        )}
                    </Box>
                )}
                {!props.hideHoldersTable && (
                <Box sx={{ marginTop: '24px' }}>
                    {props.shareholders?.loading &&
                    !props.shareholders?.data ? (
                        <Grid container item xs={12} justifyContent="center">
                            <CircularProgress />
                        </Grid>
                    ) : (
                        <DataTable
                            title="Holders"
                            data={
                                props.shareholders?.data
                                    ? props.shareholders.data
                                    : []
                            }
                            columns={[
                                {
                                    label: 'Name',
                                    name: 'investor',
                                },
                                { label: 'Class', name: 'share_class' },
                                {
                                    label: 'Category',
                                    name: 'share_category',
                                },
                                {
                                    label: 'Assets',
                                    name: 'shares',
                                    options: {
                                        customBodyRender: (value) => (
                                            <span>{formatNumber(value)}</span>
                                        ),
                                    },
                                },
                                {
                                    label: 'Assets %',
                                    name: 'shares_p',
                                    options: {
                                        customBodyRender: (value) => (
                                            <span>
                                                {Humanize.formatNumber(
                                                    value,
                                                    2
                                                )}
                                            </span>
                                        ),
                                    },
                                },
                                {
                                    label: 'Actions',
                                    name: 'ct_id',
                                    options: {
                                        download: false,
                                        print: false,
                                        customBodyRenderLite: (dataIndex) => {
                                            const row =
                                                props.shareholders.data[
                                                    dataIndex
                                                ];
                                            return (
                                                <>
                                                    <Tooltip title="Edit in Manage Holders">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                history.push(
                                                                    '/issuer/issue/cap-table/holder-info'
                                                                )
                                                            }
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                displayDeleteHolderDialog(
                                                                    row
                                                                )
                                                            }
                                                        >
                                                            <DeleteForeverIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            );
                                        },
                                    },
                                },
                            ]}
                        />
                    )}
                </Box>
                )}
            </Grid>
            <Dialog
                maxWidth="sm"
                fullWidth
                open={Boolean(deleteHolderData)}
                onBackdropClick={closeDeleteHolderDialog}
            >
                <DialogTitle className={classes.dialogTitle}>
                    Delete Holder
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete{' '}
                        {deleteHolderData?.investor}?
                    </Typography>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        color="secondary"
                        variant="outlined"
                        className={classes.secondaryButtonLabel}
                        onClick={closeDeleteHolderDialog}
                    >
                        Cancel
                    </Button>
                    {!isDeleting ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            className={classes.buttonLabel}
                            onClick={deleteHolder}
                        >
                            Delete
                        </Button>
                    ) : (
                        <CircularProgress size={24} />
                    )}
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

const mapStateToProps = ({ profile, kyc, capTable }) => {
    const totalShareholders = kyc?.data?.data?.total_shareholders ?? 0;
    const totalKyc = kyc?.data?.data?.total_kyc ?? 0;
    return {
        profile: profile,
        kyc: {
            total_shareholders: totalShareholders,
            total_kyc: totalKyc,
            total_not_kyc: totalShareholders - totalKyc,
            total_investors_registered:
                kyc?.data?.data?.total_investors_registered ?? 0,
            loading: kyc?.loading,
        },
        shareholders: capTable?.shareholders,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        kycActions: bindActionCreators(kycActions, dispatch),
        capTableActions: bindActionCreators(capTableActions, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Overview);
